import React, { createContext, useContext, useState, useEffect } from 'react';
import { AUTH_URL } from '../utils/enum';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    AuthProvider();
  } else {
    return context;
  }  
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // <-- track auth check status

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser)); // quick restore for UI
    }

    const checkToken = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${AUTH_URL}/verify-token`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Invalid token');
        const data = await response.json();
        setUser(data.user);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false); // auth check done
      }
    };

    checkToken();
  }, []);

const login = async ({ email, password, role, googleId = null, username }) => {
  try {
    // Build request body depending on login type
    const body = googleId
      ? { google_id: googleId, role, username,email } // Google login
      : { username: email, password, role }; // Email/password login

    const response = await fetch(`${AUTH_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error("Login failed");
    const data = await response.json();

    const newUser = {
      id: data.user.id,
      name: data.user.username,
      email: data.user.username,
      role,
      token: data.token,
      profileImage:
        data.user.profileImage ||
        `https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop`,
    };

    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
    localStorage.setItem("token", data.token);

    return true;
  } catch (error) {
    console.error("Login error:", error.message);
    return false;
  }
};

const register = async ({ email, password, role, googleId = null, ...extraData }) => {
  try {
    // Build request body based on login method
    const body = googleId
      ? { google_id: googleId, role, ...extraData } // Google registration
      : { username: email, password, role, ...extraData }; // Normal registration

    const response = await fetch(`${AUTH_URL}/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error("Registration failed");

    const data = await response.json();

    // Optional: auto-login after register
    if (data.token && data.user) {
      const newUser = {
        id: data.user.id,
        name: data.user.username,
        email: data.user.username,
        role,
        token: data.token,
        profileImage:
          data.user.profileImage ||
          `https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop`,
      };

      setUser(newUser);
      localStorage.setItem("user", JSON.stringify(newUser));
      localStorage.setItem("token", data.token);
    }

    return true;
  } catch (error) {
    console.error("Register error:", error.message);
    return false;
  }
};


  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        register,
        isAuthenticated: !!user,
        token: user?.token || null,
        loading, // <-- provide loading state
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};